import { useState } from "react";
import { useAccount } from "@/hooks/useAccount";
import { fmtCurrency, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowDownToLine, ArrowUpFromLine, Wallet } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "all", label: "All" },
  { key: "deposits", label: "Deposits" },
  { key: "withdrawals", label: "Withdrawals" },
];

function Stat({ label, value, icon: Icon, tone }) {
  const map = { green: "text-[#059669] bg-emerald-50", red: "text-[#dc2626] bg-red-50", dark: "text-white bg-[#15803d]" };
  const dark = tone === "dark";
  return (
    <Card className={`p-6 border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] ${dark ? "bg-[#15803d] text-white border-0" : "bg-white"}`}>
      <div className="flex items-center justify-between mb-3">
        <p className={`text-xs font-semibold uppercase tracking-[0.14em] ${dark ? "text-white/60" : "text-slate-500"}`}>{label}</p>
        <div className={`w-9 h-9 rounded-2xl flex items-center justify-center ${dark ? "bg-white/15 text-white" : map[tone]}`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
      </div>
      <p className="text-3xl font-bold tabular-nums">{value}</p>
    </Card>
  );
}

export default function Cashflow() {
  const { data, isLoading } = useAccount();
  const [tab, setTab] = useState("all");

  if (isLoading) return <div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 rounded-2xl" /></div>;

  const s = data?.summary || {};
  const deposits = (data?.cashflows?.deposits || []).map((t) => ({ ...t, kind: "Deposit" }));
  const withdrawals = (data?.cashflows?.withdrawals || []).map((t) => ({ ...t, kind: "Withdrawal" }));
  let rows;
  if (tab === "deposits") rows = deposits;
  else if (tab === "withdrawals") rows = withdrawals;
  else rows = [...deposits, ...withdrawals];
  rows = rows.sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="space-y-8" data-testid="cashflow-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="overline mb-1">Money Movement</p>
          <h1 className="font-serif text-4xl font-bold text-slate-900">Deposits &amp; Withdrawals</h1>
        </div>
        <Button data-testid="request-btn" onClick={() => toast.success("Request sent to your advisor for approval")}
          className="rounded-full bg-[#15803d] hover:bg-[#166534] text-white transition-colors duration-200">
          Request a transaction
        </Button>
      </div>

      <div className="grid gap-6 sm:grid-cols-3 fade-up">
        <Stat label="Total Deposited" value={fmtCurrency(s.total_deposited)} icon={ArrowDownToLine} tone="green" />
        <Stat label="Total Withdrawn" value={fmtCurrency(s.total_withdrawn)} icon={ArrowUpFromLine} tone="red" />
        <Stat label="Net Returns Generated" value={fmtCurrency(s.net_profit)} icon={Wallet} tone="dark" />
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 w-fit fade-up">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`cashflow-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
              tab === t.key ? "bg-[#15803d] text-white" : "text-slate-500 hover:text-slate-800"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card className="bg-white border-slate-200 shadow-[0_2px_8px_-2px_rgba(15,23,42,0.08)] overflow-hidden fade-up">
        {rows.length === 0 ? (
          <div className="p-12 text-center text-slate-500">No records.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f6f7f4] hover:bg-[#f6f7f4]">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t, i) => {
                const inflow = t.kind === "Deposit";
                return (
                  <TableRow key={t.id || i} data-testid={`cashflow-row-${i}`} className="hover:bg-slate-50 transition-colors duration-200">
                    <TableCell className="text-slate-600 tabular-nums">{fmtDate(t.date)}</TableCell>
                    <TableCell>
                      <Badge className={`${inflow ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} border-0 font-medium`}>{t.kind}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-700">{t.description}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${inflow ? "text-[#059669]" : "text-[#dc2626]"}`}>
                      {inflow ? "+" : "-"}{fmtCurrency(t.amount)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
